import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { FileText, Eye, Upload } from "lucide-react";

/** Active official letterhead (quotes / POs / email art): info, full-page preview, super-admin swap with automatic backup. */
export const LetterheadCard = () => {
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [open, setOpen] = useState(false);
  const [stamp, setStamp] = useState(Date.now());
  const fileRef = useRef(null);
  const load = () => api.get("/comms/branding/letterhead-info").then(({ data }) => { setInfo(data); setStamp(Date.now()); }).catch(() => {});
  useEffect(() => { load(); }, []);
  const previewUrl = `${process.env.REACT_APP_BACKEND_URL}/api/comms/branding/letterhead-preview?dpi=90&t=${stamp}`;

  const upload = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") return toast.error("Please choose an A4 PDF letterhead");
    if (!window.confirm(`Replace the official letterhead with "${file.name}"? The current file is backed up automatically and all new quotations / POs will use the new artwork.`)) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try { const { data } = await api.put("/comms/branding/letterhead", { data_url: reader.result, file_name: file.name }); setInfo(data); setStamp(Date.now()); toast.success("Letterhead replaced — header/footer art regenerated"); }
      catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    };
    reader.readAsDataURL(file);
  };

  if (!info) return null;
  return (
    <div className="border border-slate-200 rounded-md p-3 bg-white text-[12px] space-y-1.5" data-testid="comms-letterhead-card">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-[11px] uppercase text-slate-400"><FileText className="h-3.5 w-3.5" />Official letterhead (quotes · POs · email art)</div>
        <span className="text-[10px] text-slate-400">A4 · {info.width_pt}×{info.height_pt} pt · {Math.round(info.size_bytes / 1024)} KB</span>
      </div>
      <div className="flex gap-3">
        <img src={previewUrl} alt="Active letterhead preview" className="w-20 border border-slate-200 rounded cursor-pointer" onClick={() => setOpen(true)} data-testid="comms-letterhead-thumb" />
        <div className="space-y-0.5">
          <div><span className="text-slate-500">File:</span> <span className="font-medium" data-testid="comms-letterhead-file">{info.file_name}</span></div>
          <div><span className="text-slate-500">Updated:</span> {String(info.updated_at).slice(0, 16).replace("T", " ")}{info.updated_by ? ` by ${info.updated_by}` : ""}</div>
          <div className="text-slate-500">Header art {info.header_art_mm} mm → content starts {info.content_top_mm} mm · footer art {info.footer_art_mm} mm → content ends {info.content_bottom_mm} mm from the bottom</div>
          <div className="text-slate-400">{info.used_for.join(" · ")}</div>
        </div>
      </div>
      <div className="flex gap-2 pt-1">
        <Button size="sm" variant="outline" className="h-7 gap-1 text-[11px]" onClick={() => setOpen(true)} data-testid="comms-letterhead-preview"><Eye className="h-3.5 w-3.5" />Preview letterhead</Button>
        {user?.role === "super_admin" && (<>
          <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="comms-letterhead-file-input" />
          <Button size="sm" variant="outline" className="h-7 gap-1 text-[11px]" onClick={() => fileRef.current?.click()} data-testid="comms-letterhead-replace"><Upload className="h-3.5 w-3.5" />Replace (A4 PDF)…</Button>
        </>)}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="comms-letterhead-dialog">
          <DialogHeader><DialogTitle className="text-base">Active letterhead — {info.file_name}</DialogTitle></DialogHeader>
          <img src={previewUrl} alt="Letterhead page 1" className="w-full border border-slate-200 rounded" />
          <div className="text-[11px] text-slate-500">Content area used by quotations: {info.content_top_mm} mm from top to {info.content_bottom_mm} mm from bottom. Previous files are kept as dated backups in the assets folder.</div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
